import React, { useState, useContext, useEffect } from "react";
import ApiService from "../../ApiService";
import { Container, Row, Col, Card, Form, Button, Modal } from "react-bootstrap";
import { IoSend, IoPencil } from "react-icons/io5";
import statesList from "../../util/statesList";
import { SnackbarContext } from "../AlertSystem";
import { MonitorService } from "../../util/captureException";
import { PREP_PARTNER_OPTIONS } from "../../constants/prepPartnerOptions";

const isProd = process.env.REACT_APP_ENV === "prod";

// ─── Program selection: two dimensions (program vs maintenance, free vs paid) ───
const PROGRAM_OR_MAINTENANCE_OPTIONS = [
  { key: "program", label: "PrEP Program" },
  { key: "maintenance", label: "PrEP Maintenance" },
];
const FREE_OR_PAID_OPTIONS = [
  { key: "free", label: "Free" },
  { key: "paid", label: "Paid" },
];

const SEX_AT_BIRTH_OPTIONS = [
  { key: "male", label: "Assigned Male At Birth", shortLabel: "Male" },
  { key: "female", label: "Assigned Female At Birth", shortLabel: "Female" },
  { key: "intersex", label: "Assigned Intersex At Birth", shortLabel: "Intersex" },
];

// ─── Product variant IDs: PRODUCT_VARIANTS[programType][sexAtBirth] → Saleor variantId
const PRODUCT_VARIANTS = isProd
  ? {
      paid_prep_program: {
        male: "UHJvZHVjdFZhcmlhbnQ6MjA5",
        female: "UHJvZHVjdFZhcmlhbnQ6MjEw",
        intersex: "UHJvZHVjdFZhcmlhbnQ6MjEx",
      },
      free_prep_program: {
        male: "UHJvZHVjdFZhcmlhbnQ6MjA3",
        female: "UHJvZHVjdFZhcmlhbnQ6MjA2",
        intersex: "UHJvZHVjdFZhcmlhbnQ6MjA4",
      },
      paid_prep_maintenance: {
        male: "UHJvZHVjdFZhcmlhbnQ6MjIx",
        female: "UHJvZHVjdFZhcmlhbnQ6MjIy",
        intersex: "UHJvZHVjdFZhcmlhbnQ6MjIz",
      },
      free_prep_maintenance: {
        male: "UHJvZHVjdFZhcmlhbnQ6MjMy",
        female: "UHJvZHVjdFZhcmlhbnQ6MjMz",
        intersex: "UHJvZHVjdFZhcmlhbnQ6MjM0",
      },
    }
  : {
      paid_prep_program: {
        male: "UHJvZHVjdFZhcmlhbnQ6NDQ4",
        female: "UHJvZHVjdFZhcmlhbnQ6NDQ5",
        intersex: "UHJvZHVjdFZhcmlhbnQ6NDUw",
      },
      free_prep_program: {
        male: "UHJvZHVjdFZhcmlhbnQ6NDQx",
        female: "UHJvZHVjdFZhcmlhbnQ6NDQy",
        intersex: "UHJvZHVjdFZhcmlhbnQ6NDQz",
      },
      paid_prep_maintenance: {
        male: "UHJvZHVjdFZhcmlhbnQ6NDYz",
        female: "UHJvZHVjdFZhcmlhbnQ6NDY0",
        intersex: "UHJvZHVjdFZhcmlhbnQ6NDY1",
      },
      free_prep_maintenance: {
        male: "UHJvZHVjdFZhcmlhbnQ6NDcw",
        female: "UHJvZHVjdFZhcmlhbnQ6NDY5",
        intersex: "UHJvZHVjdFZhcmlhbnQ6NDY4",
      },
    };

// ─── Partners ───────────────────────────────────────────────────────────────────
// TODO: Replace with API call (e.g., ApiService.getPartners()) when endpoint is available.
// Same IDs as PrEP dashboard filter — must match Order.partnerId in DynamoDB.
const PARTNERS = PREP_PARTNER_OPTIONS;

// ─── Vendors (lab/fulfillment) ──────────────────────────────────────────────────
const VENDORS = [
  { id: "IHD", name: "IHD" },
  { id: "LABCORP", name: "LABCORP" },
];

// ─── Shipping methods ───────────────────────────────────────────────────────────
const SHIPPING = isProd
  ? [
      { name: "USPS Priority (1-3 Days) - $10", variantId: "U2hpcHBpbmdNZXRob2Q6Nw==" },
      { name: "USPS Priority (1-3 Days) - $0", variantId: "U2hpcHBpbmdNZXRob2Q6Njc=" },
    ]
  : [
      { name: "USPS Priority (1-3 Days) - $10", variantId: "U2hpcHBpbmdNZXRob2Q6NTUx" },
      { name: "USPS Priority (1-3 Days) - $0", variantId: "U2hpcHBpbmdNZXRob2Q6NTYy" },
    ];

// ─── Legacy product names for kitName URL param matching (backward compat) ──────
const LEGACY_PRODUCT_NAMES = {
  paid_prep_program: "Paid PrEP HIV Prevention Program",
  free_prep_program: "Free PrEP Program At-home STI Test",
  paid_prep_maintenance: "PrEP Maintenance At-home STI Test",
  free_prep_maintenance: "Free PrEP Maintenance At-home STI Test",
};

// Valid keys for variant lookup (single source of truth; no typos)
const PROGRAM_TYPE_KEYS = ["paid_prep_program", "free_prep_program", "paid_prep_maintenance", "free_prep_maintenance"];
const SEX_AT_BIRTH_KEYS = ["male", "female", "intersex"];

/** Single source of truth for product variant ID. Returns Saleor variantId only when valid (non-empty, not PLACEHOLDER). */
function getVariantId(programType, sexAtBirth) {
  if (!programType || !sexAtBirth) return null;
  if (!PROGRAM_TYPE_KEYS.includes(programType) || !SEX_AT_BIRTH_KEYS.includes(sexAtBirth)) return null;
  const row = PRODUCT_VARIANTS[programType];
  if (!row) return null;
  const id = row[sexAtBirth];
  if (!id || typeof id !== "string" || id.trim() === "" || id.startsWith("PLACEHOLDER")) return null;
  return id;
}

function isValidVariantId(id) {
  return !!id && typeof id === "string" && id.trim() !== "" && !id.startsWith("PLACEHOLDER");
}

// Flat list for kitName URL matching: only entries with a valid variant ID (excludes prod free_prep_maintenance placeholders)
const ALL_PRODUCTS_FLAT = [];
PROGRAM_TYPE_KEYS.forEach(progKey => {
  SEX_AT_BIRTH_OPTIONS.forEach(sex => {
    const variantId = getVariantId(progKey, sex.key);
    if (variantId) {
      ALL_PRODUCTS_FLAT.push({
        programType: progKey,
        sexAtBirth: sex.key,
        name: `${LEGACY_PRODUCT_NAMES[progKey]} (${sex.label})`,
        variantId,
      });
    }
  });
});

// Normalize phone to +1 followed by 10 digits for backend (e.g. +17987456214)
const PHONE_FORMAT = /^\+1\d{10}$/;
function normalizePhoneToPlus1(raw) {
  const digits = (raw || "").replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === "1") return `+${digits}`;
  return null;
}

// ─── Component ──────────────────────────────────────────────────────────────────
const CreatePrepOrder = ({ resetView }) => {
  const { showSnackbar } = useContext(SnackbarContext);

  // Product selection: program vs maintenance, free vs paid, then sex at birth
  const [programOrMaintenance, setProgramOrMaintenance] = useState("");
  const [freeOrPaid, setFreeOrPaid] = useState("");
  const [sexAtBirth, setSexAtBirth] = useState("");

  // Partner & vendor (both required; default to first option)
  const [partnerId, setPartnerId] = useState(PARTNERS[0]?.id || "");
  const [vendorId, setVendorId] = useState(VENDORS[0]?.id || "");

  // Confirmation step before creating order
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // When product is fully selected, show summary + Change; click Change to show selection buttons again
  const [isEditingProgram, setIsEditingProgram] = useState(false);

  // Derive programType for PRODUCT_VARIANTS lookup: paid_prep_program | free_prep_program | paid_prep_maintenance | free_prep_maintenance
  const programType =
    programOrMaintenance && freeOrPaid
      ? `${freeOrPaid}_prep_${programOrMaintenance === "program" ? "program" : "maintenance"}`
      : "";

  const hasProgramSelection = !!(programType && sexAtBirth);
  // Variant ID: single source of truth; never use any other path for the ID sent to the API
  const resolvedVariantId = getVariantId(programType, sexAtBirth) ?? "";
  const hasValidVariant = isValidVariantId(resolvedVariantId);
  const showProgramSummary = hasProgramSelection && !isEditingProgram && hasValidVariant;

  // Progressive steps: next section appears when current is "done"; step1 requires a valid variant (no placeholders)
  const [isEditingPartnerVendor, setIsEditingPartnerVendor] = useState(false);
  const [isEditingPatient, setIsEditingPatient] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isEditingShipping, setIsEditingShipping] = useState(false);

  const step1Done = hasProgramSelection && hasValidVariant;

  // Patient info
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Address
  const [streetAddress, setStreetAddress] = useState("");
  const [streetAddress2, setStreetAddress2] = useState("");
  const [locality, setLocality] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");

  // Shipping & payment
  const [shippingMethodId, setShippingMethodId] = useState(SHIPPING[1]?.variantId || "");
  const [invoiceAmount, setInvoiceAmount] = useState(0);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Patient lookup: show lookup step when no patient loaded; support email or patientId in URL
  const [patientLoaded, setPatientLoaded] = useState(false);
  const [lookupInput, setLookupInput] = useState("");
  const [lookupBy, setLookupBy] = useState("email"); // "email" | "patientId"
  const [initialUrlCheckDone, setInitialUrlCheckDone] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const patientIdFromUrl = urlParams.get("patientId");
  const emailFromUrl = urlParams.get("email");
  const kitName = urlParams.get("kitName");

  // step2Done is only true when user has completed Section 1 (so progressive disclosure isn't skipped)
  const step2Done = step1Done && !!(partnerId && vendorId);
  const step3Done = !!(firstName.trim() && lastName.trim() && email.trim() && phone.trim());
  const step4Done = !!(streetAddress.trim() && locality.trim() && region.trim() && postalCode.trim());
  const step5Done = !!shippingMethodId;
  const allStepsDone = step1Done && step2Done && step3Done && step4Done && step5Done;

  // Auto-default to $0 shipping when Free is selected
  useEffect(() => {
    if (freeOrPaid === "free") {
      const freeShipping = SHIPPING.find(s => s.name.includes("$0"));
      if (freeShipping) setShippingMethodId(freeShipping.variantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freeOrPaid]);

  // Fill form from API patient payload; optionally apply kitName product matching
  // getPatientDetailByEmail returns givenName/familyName; getPatientDetailsBySubId returns firstName/lastName
  const fillFormFromPatientData = (data, applyKitMatch = true) => {
    if (!data) return;
    setFirstName(data.firstName || data.givenName || "");
    setLastName(data.lastName || data.familyName || "");
    setEmail(data.email || "");
    setPhone(data.phoneNumber || "");
    setStreetAddress(data.address?.street_address || "");
    setStreetAddress2(data.address?.street_address2 || "");
    setLocality(data.address?.locality || "");
    setRegion(data.address?.region || "");
    setPostalCode(data.address?.postal_code || "");
    if (applyKitMatch && kitName) {
      const normalizedKitName = kitName.toLowerCase().replace(/[^a-z0-9]/gi, "");
      const candidates = ALL_PRODUCTS_FLAT.filter(product => {
        const normalizedProductName = product.name.toLowerCase().replace(/[^a-z0-9]/gi, "");
        return (
          normalizedKitName.includes(normalizedProductName) ||
          normalizedProductName.includes(normalizedKitName)
        );
      });
      // Pick most specific match (longest product name) to avoid e.g. "free prep program" matching when kitName was "free prep maintenance"
      const matchedProduct = candidates.length
        ? candidates.reduce((best, p) => {
            const bestLen = (best.name || "").length;
            const pLen = (p.name || "").length;
            return pLen > bestLen ? p : best;
          })
        : null;
      if (matchedProduct) {
        const pt = matchedProduct.programType;
        setFreeOrPaid(pt.startsWith("paid_") ? "paid" : "free");
        setProgramOrMaintenance(pt.includes("maintenance") ? "maintenance" : "program");
        setSexAtBirth(matchedProduct.sexAtBirth);
      }
    }
  };

  const fetchPatientByEmail = async (emailToUse) => {
    const trimmed = (emailToUse || lookupInput || "").trim();
    if (!trimmed) {
      showSnackbar({ message: "Please enter an email address", severity: "warning" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      showSnackbar({ message: "Please enter a valid email address", severity: "warning" });
      return;
    }
    setIsLoading(true);
    try {
      const res = await ApiService.getPatientDetailByEmail(encodeURIComponent(trimmed));
      if (res?.data) {
        fillFormFromPatientData(res.data);
        setPatientLoaded(true);
        setLookupInput("");
        const pid = res.data.sub || res.data.userId || res.data.id || "";
        const params = new URLSearchParams(window.location.search);
        params.set("page", "CreatePrepOrder");
        if (pid) params.set("patientId", pid);
        if (kitName) params.set("kitName", kitName);
        const qs = params.toString();
        window.history.replaceState({}, "", `${window.location.pathname}?${qs}`);
        showSnackbar({ message: "Patient found. You can edit details below if needed.", severity: "success" });
      } else {
        showSnackbar({ message: "No patient found for this email.", severity: "warning" });
      }
    } catch (error) {
      showSnackbar({ message: "Could not find patient. Check the email or try again.", severity: "error" });
      MonitorService.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPatientById = async (idToUse) => {
    const trimmed = (idToUse || lookupInput || "").trim();
    if (!trimmed) {
      showSnackbar({ message: "Please enter a Patient ID", severity: "warning" });
      return;
    }
    setIsLoading(true);
    try {
      const res = await ApiService.getPatientDetailsBySubId(trimmed);
      if (res?.data) {
        fillFormFromPatientData(res.data);
        setPatientLoaded(true);
        setLookupInput("");
        const params = new URLSearchParams(window.location.search);
        params.set("page", "CreatePrepOrder");
        params.set("patientId", trimmed);
        if (kitName) params.set("kitName", kitName);
        window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
        showSnackbar({ message: "Patient found. You can edit details below if needed.", severity: "success" });
      } else {
        showSnackbar({ message: "No patient found for this ID.", severity: "warning" });
      }
    } catch (error) {
      showSnackbar({ message: "Could not find patient. Check the ID or try again.", severity: "error" });
      MonitorService.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLookup = () => {
    if (lookupBy === "email") fetchPatientByEmail();
    else fetchPatientById();
  };

  const handleChangePatient = () => {
    setPatientLoaded(false);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setStreetAddress("");
    setStreetAddress2("");
    setLocality("");
    setRegion("");
    setPostalCode("");
    setLookupInput("");
    setIsEditingPatient(false);
    setIsEditingAddress(false);
    setIsEditingShipping(false);
    setIsEditingPartnerVendor(false);
    const params = new URLSearchParams(window.location.search);
    params.set("page", "CreatePrepOrder");
    params.delete("patientId");
    if (kitName) params.set("kitName", kitName);
    window.history.replaceState({}, "", `${window.location.pathname}?${params.toString()}`);
    showSnackbar({ message: "Enter patient email or ID to look up, or fill the form manually below.", severity: "info" });
  };

  const handleEnterDetailsManually = () => {
    setPatientLoaded(true);
    setLookupInput("");
  };

  // On mount: if email or patientId in URL, fetch and fill; otherwise show lookup step
  useEffect(() => {
    if (initialUrlCheckDone) return;
    setInitialUrlCheckDone(true);

    if (emailFromUrl?.trim()) {
      setLookupInput(emailFromUrl.trim());
      fetchPatientByEmail(emailFromUrl.trim());
      return;
    }
    if (patientIdFromUrl?.trim()) {
      setLookupBy("patientId");
      setLookupInput(patientIdFromUrl.trim());
      fetchPatientById(patientIdFromUrl.trim());
      return;
    }
    setPatientLoaded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Styles (dashboard: clean, consistent spacing, clear hierarchy) ───────────
  const inputStyle = {
    background: "#fafafa",
    border: "1px solid #e0e0e0",
    boxShadow: "none",
    padding: "12px 16px",
    borderRadius: "8px",
    color: "#333",
    width: "100%",
    marginTop: "6px",
    height: "44px",
    fontSize: "0.95rem",
  };

  const labelStyle = {
    color: "#444",
    fontWeight: "600",
    marginBottom: "8px",
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.02em",
    display: "block",
  };

  const sectionHeadingStyle = {
    color: "#222",
    fontWeight: "600",
    fontSize: "1rem",
    marginBottom: "20px",
    paddingBottom: "10px",
    borderBottom: "2px solid #007F61",
    display: "block",
    width: "100%",
  };

  const sectionBlockStyle = { marginBottom: "32px" };

  // Selected = solid green, white text; unselected = light border
  const getOptionStyle = (currentValue, optionKey) => {
    const isSelected = currentValue === optionKey;
    return {
      flex: 1,
      padding: "14px 18px",
      border: isSelected ? "2px solid #007F61" : "1px solid #dee2e6",
      borderRadius: "8px",
      background: isSelected ? "#007F61" : "#fff",
      color: isSelected ? "#fff" : "#495057",
      fontWeight: isSelected ? "600" : "500",
      cursor: "pointer",
      textAlign: "center",
      transition: "all 0.2s ease",
      fontSize: "0.95rem",
      boxShadow: isSelected ? "0 2px 6px rgba(0, 127, 97, 0.25)" : "none",
    };
  };

  const summaryBoxStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "14px 18px",
    background: "#e8f5f1",
    borderRadius: "8px",
    border: "1px solid #b8dfd3",
  };
  const changeButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    background: "transparent",
    border: "1px solid #007F61",
    borderRadius: "6px",
    color: "#007F61",
    fontSize: "0.85rem",
    fontWeight: "500",
    cursor: "pointer",
  };

  const getSexButtonStyle = sexKey => {
    const isSelected = sexAtBirth === sexKey;
    return {
      flex: 1,
      padding: "12px 10px",
      border: isSelected ? "2px solid #007F61" : "1px solid #dee2e6",
      borderRadius: "8px",
      background: isSelected ? "#007F61" : "#fff",
      color: isSelected ? "#fff" : "#495057",
      fontWeight: isSelected ? "600" : "500",
      cursor: "pointer",
      textAlign: "center",
      transition: "all 0.2s ease",
      fontSize: "0.9rem",
      boxShadow: isSelected ? "0 2px 6px rgba(0, 127, 97, 0.25)" : "none",
    };
  };

  // Full reset (Clear button): clear all fields and return to patient lookup
  const clearForm = () => {
    setProgramOrMaintenance("");
    setFreeOrPaid("");
    setSexAtBirth("");
    setPartnerId(PARTNERS[0]?.id || "");
    setVendorId(VENDORS[0]?.id || "");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setStreetAddress("");
    setStreetAddress2("");
    setLocality("");
    setRegion("");
    setPostalCode("");
    setShippingMethodId(SHIPPING[1]?.variantId || "");
    setInvoiceAmount(0);
    setPatientLoaded(false);
    setLookupInput("");
    setIsEditingProgram(false);
    setIsEditingPartnerVendor(false);
    setIsEditingPatient(false);
    setIsEditingAddress(false);
    setIsEditingShipping(false);
    window.history.replaceState({}, "", `${window.location.pathname}?page=CreatePrepOrder`);
  };

  // Reset only order fields after successful submit (keep patient for another order)
  const clearOrderOnly = () => {
    setProgramOrMaintenance("");
    setFreeOrPaid("");
    setSexAtBirth("");
    setShippingMethodId(SHIPPING[1]?.variantId || "");
    setInvoiceAmount(0);
    setIsEditingProgram(false);
    setIsEditingPartnerVendor(false);
    setIsEditingPatient(false);
    setIsEditingAddress(false);
    setIsEditingShipping(false);
  };

  // ── Validation & Submit ─────────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault();

    const fName = firstName.trim();
    const lName = lastName.trim();
    const userEmail = email.trim();
    const userPhone = phone.trim();
    const street = streetAddress.trim();
    const city = locality.trim();
    const state = region.trim();
    const zip = postalCode.trim();

    if (!programOrMaintenance) {
      showSnackbar({ message: "Please select Program or Maintenance", severity: "warning" });
      return;
    }
    if (!freeOrPaid) {
      showSnackbar({ message: "Please select Free or Paid", severity: "warning" });
      return;
    }
    if (!sexAtBirth) {
      showSnackbar({ message: "Please select sex assigned at birth", severity: "warning" });
      return;
    }
    if (!getVariantId(programType, sexAtBirth)) {
      showSnackbar({ message: "This program option is not yet available. Please select another program.", severity: "warning" });
      return;
    }
    if (!partnerId) {
      showSnackbar({ message: "Please select a partner", severity: "warning" });
      return;
    }
    if (!vendorId) {
      showSnackbar({ message: "Please select a vendor", severity: "warning" });
      return;
    }
    if (!fName) {
      showSnackbar({ message: "First name is required", severity: "warning" });
      return;
    }
    if (!lName) {
      showSnackbar({ message: "Last name is required", severity: "warning" });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!userEmail || !emailRegex.test(userEmail)) {
      showSnackbar({ message: "Valid email is required", severity: "warning" });
      return;
    }
    const normalizedPhone = normalizePhoneToPlus1(userPhone);
    if (!normalizedPhone || !PHONE_FORMAT.test(normalizedPhone)) {
      showSnackbar({ message: "Phone must be in format +17987456214 (+1 then 10 digits)", severity: "warning" });
      return;
    }
    setPhone(normalizedPhone);

    if (!street) {
      showSnackbar({ message: "Street address is required", severity: "warning" });
      return;
    }
    if (!city) {
      showSnackbar({ message: "City is required", severity: "warning" });
      return;
    }
    if (!state) {
      showSnackbar({ message: "State is required", severity: "warning" });
      return;
    }
    if (!zip) {
      showSnackbar({ message: "Postal code is required", severity: "warning" });
      return;
    }
    if (!shippingMethodId) {
      showSnackbar({ message: "Please select a shipping method", severity: "warning" });
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmAndCreateOrder = async () => {
    setIsSubmitting(true);
    try {
      await createSaleorOrder();
      setShowConfirmModal(false);
    } catch {
      // Keep modal open on error so user can retry without losing context
    } finally {
      setIsSubmitting(false);
    }
  };

  const createSaleorOrder = async () => {
    // Resolve variant ID at submit time from current state; never use a cached value so it cannot be wrong
    const payloadVariantId = getVariantId(programType, sexAtBirth);
    if (!payloadVariantId) {
      showSnackbar({ message: "Invalid product selection. Please go back and choose a valid program.", severity: "error" });
      throw new Error("Invalid variant: no valid variant ID for current program selection");
    }

    const phoneForPayload = normalizePhoneToPlus1(phone);
    if (!phoneForPayload || !PHONE_FORMAT.test(phoneForPayload)) {
      showSnackbar({ message: "Phone must be in format +17987456214 (+1 then 10 digits)", severity: "error" });
      throw new Error("Invalid phone format");
    }
    try {
      const payload = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phoneForPayload,
        variantId: payloadVariantId,
        userAddress: {
          street_address: streetAddress.trim(),
          street_address2: streetAddress2.trim(),
          locality: locality.trim(),
          region: region.trim(),
          postal_code: postalCode.trim(),
        },
        shippingMethodId,
        partnerId,
        vendorId,
        amountPaid: invoiceAmount ? Number(invoiceAmount) : null,
      };

      await ApiService.createSaleorOrder(payload);
      showSnackbar({
        message: "Order created successfully! You can place another order for this patient or click Change patient.",
        severity: "success",
      });
      clearOrderOnly();
    } catch (error) {
      console.error("Error creating order:", error);
      showSnackbar({ message: "Failed to create order. Please verify the patient information and address, then try again.", severity: "error" });
      MonitorService.captureException(error);
      throw error;
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Container fluid>
      {/* Back & Clear buttons */}
      <Row className="mb-4">
        <Col>
          <Button variant="outline-secondary" onClick={resetView} style={{ marginRight: "8px" }}>
            &larr; Back
          </Button>
          <Button variant="outline-danger" onClick={clearForm}>
            Clear
          </Button>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <div style={{ textAlign: "center", marginBottom: "8px", fontSize: "26px", fontWeight: "600", color: "#222" }}>
            Create PrEP Order
          </div>
          <div style={{ textAlign: "center", color: "#6c757d", marginBottom: "28px", fontSize: "0.95rem" }}>
            {patientLoaded
              ? "Select a program, fill in any missing details, and submit."
              : "Look up a patient by email or ID, or enter details manually."}
          </div>

          {/* ── Patient lookup step (when no patient loaded) ── */}
          {!patientLoaded && (
            <Card
              style={{
                background: "#fff",
                color: "#333",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                padding: "30px",
                border: "1px solid #e0e0e0",
                borderTop: "5px solid #007F61",
                marginBottom: "24px",
              }}
            >
              <Card.Body>
                <div style={sectionHeadingStyle}>Find patient</div>
                <p style={{ color: "#555", fontSize: "0.9rem", marginBottom: "16px" }}>
                  Enter the patient&apos;s email or Patient ID to pre-fill their details. You can also enter details manually below.
                </p>

                {isLoading ? (
                  <div style={{ textAlign: "center", padding: "32px" }}>
                    <div className="spinner-border text-success" role="status" aria-label="Loading" style={{ width: "2.5rem", height: "2.5rem" }} />
                  </div>
                ) : (
                  <>
                    <Form.Group style={{ marginBottom: "12px" }}>
                      <Form.Label style={labelStyle}>Look up by</Form.Label>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <Button
                          variant={lookupBy === "email" ? "primary" : "outline-primary"}
                          size="sm"
                          onClick={() => setLookupBy("email")}
                          style={lookupBy === "email" ? { background: "#007F61", borderColor: "#007F61" } : {}}
                        >
                          Email
                        </Button>
                        <Button
                          variant={lookupBy === "patientId" ? "primary" : "outline-primary"}
                          size="sm"
                          onClick={() => setLookupBy("patientId")}
                          style={lookupBy === "patientId" ? { background: "#007F61", borderColor: "#007F61" } : {}}
                        >
                          Patient ID
                        </Button>
                      </div>
                    </Form.Group>
                    <Form.Group style={{ marginBottom: "16px" }}>
                      <Form.Label style={labelStyle}>
                        {lookupBy === "email" ? "Patient email" : "Patient ID"}
                      </Form.Label>
                      <Form.Control
                        type={lookupBy === "email" ? "email" : "text"}
                        placeholder={lookupBy === "email" ? "patient@example.com" : "Enter Patient ID"}
                        value={lookupInput}
                        onChange={e => setLookupInput(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && (e.preventDefault(), handleLookup())}
                        style={inputStyle}
                      />
                    </Form.Group>
                    <Button
                      onClick={handleLookup}
                      disabled={!lookupInput.trim()}
                      style={{
                        background: "#007F61",
                        border: "none",
                        borderRadius: "8px",
                        padding: "12px 24px",
                        fontWeight: "600",
                        marginRight: "12px",
                        marginBottom: "8px",
                      }}
                    >
                      Look up patient
                    </Button>
                    <Button variant="link" onClick={handleEnterDetailsManually} style={{ color: "#007F61", paddingLeft: "0" }}>
                      Enter details manually instead
                    </Button>
                  </>
                )}
              </Card.Body>
            </Card>
          )}

          {patientLoaded && (
          <Card
            style={{
              background: "#fff",
              color: "#333",
              borderRadius: "12px",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
              padding: "36px 40px",
              border: "1px solid #e8eaed",
              borderTop: "4px solid #007F61",
            }}
          >
            <Card.Body style={{ padding: 0 }}>
              <Form onSubmit={handleSubmit}>
                {/* ── Section 1: Select Program (summary when selected, or selection buttons when editing) ── */}
                <div style={sectionBlockStyle}>
                  <div style={sectionHeadingStyle}>1. Select Program</div>

                  {showProgramSummary ? (
                    <div style={summaryBoxStyle}>
                      <span style={{ fontSize: "0.95rem", color: "#006b52", fontWeight: "500" }}>
                        {FREE_OR_PAID_OPTIONS.find(o => o.key === freeOrPaid)?.label}{" "}
                        {PROGRAM_OR_MAINTENANCE_OPTIONS.find(o => o.key === programOrMaintenance)?.label}
                        {" — "}
                        {SEX_AT_BIRTH_OPTIONS.find(s => s.key === sexAtBirth)?.label}
                      </span>
                      <button type="button" onClick={() => setIsEditingProgram(true)} style={changeButtonStyle} aria-label="Change program selection">
                        <IoPencil style={{ fontSize: "1rem" }} />
                        Change
                      </button>
                    </div>
                  ) : (
                    <>
                      <Form.Label style={{ ...labelStyle, marginTop: "4px" }}>Program or Maintenance</Form.Label>
                      <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
                        {PROGRAM_OR_MAINTENANCE_OPTIONS.map(opt => (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => setProgramOrMaintenance(opt.key)}
                            style={getOptionStyle(programOrMaintenance, opt.key)}
                            aria-pressed={programOrMaintenance === opt.key}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      <Form.Label style={labelStyle}>Free or Paid</Form.Label>
                      <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
                        {FREE_OR_PAID_OPTIONS.map(opt => (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => setFreeOrPaid(opt.key)}
                            style={getOptionStyle(freeOrPaid, opt.key)}
                            aria-pressed={freeOrPaid === opt.key}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      {programOrMaintenance && freeOrPaid && (
                        <>
                          <Form.Label style={labelStyle}>Sex Assigned at Birth</Form.Label>
                          <div style={{ display: "flex", gap: "12px", marginBottom: "8px" }}>
                            {SEX_AT_BIRTH_OPTIONS.map(sex => (
                              <button
                                key={sex.key}
                                type="button"
                                onClick={() => {
                                  setSexAtBirth(sex.key);
                                  setIsEditingProgram(false);
                                }}
                                style={getSexButtonStyle(sex.key)}
                                aria-pressed={sexAtBirth === sex.key}
                              >
                                {sex.shortLabel}
                              </button>
                            ))}
                          </div>
                          {hasProgramSelection && !hasValidVariant && (
                            <div style={{ marginTop: "12px", padding: "12px", background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "8px", fontSize: "0.9rem", color: "#856404" }}>
                              This program option is not yet available. Please select Free or Paid PrEP Program, or Paid PrEP Maintenance.
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>

                {/* ── Section 2: Partner & Vendor (visible after program selected) ── */}
                {step1Done && (
                  <div style={sectionBlockStyle}>
                    <div style={sectionHeadingStyle}>2. Partner &amp; Vendor</div>

                    {step2Done && !isEditingPartnerVendor ? (
                      <div style={summaryBoxStyle}>
                        <span style={{ fontSize: "0.95rem", color: "#006b52", fontWeight: "500" }}>
                          {PARTNERS.find(p => p.id === partnerId)?.name || partnerId} · {VENDORS.find(v => v.id === vendorId)?.name || vendorId}
                        </span>
                        <button type="button" onClick={() => setIsEditingPartnerVendor(true)} style={changeButtonStyle} aria-label="Change partner and vendor">
                          <IoPencil style={{ fontSize: "1rem" }} />
                          Change
                        </button>
                      </div>
                    ) : (
                      <>
                        <Row>
                          <Col md={6}>
                            <Form.Group controlId="partnerId" style={{ marginBottom: "20px" }}>
                              <Form.Label style={labelStyle}>Partner</Form.Label>
                              <Form.Select value={partnerId} onChange={e => setPartnerId(e.target.value)} style={inputStyle}>
                                <option value="">Select a partner</option>
                                {PARTNERS.map(p => (
                                  <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="vendorId" style={{ marginBottom: "20px" }}>
                              <Form.Label style={labelStyle}>Vendor</Form.Label>
                              <Form.Select value={vendorId} onChange={e => setVendorId(e.target.value)} style={inputStyle}>
                                <option value="">Select vendor</option>
                                {VENDORS.map(v => (
                                  <option key={v.id} value={v.id}>{v.name}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        {step2Done && (
                          <Button type="button" variant="outline-success" size="sm" onClick={() => setIsEditingPartnerVendor(false)} style={{ marginTop: "4px" }}>
                            Done
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── Section 3: Patient Information (visible after partner/vendor done) ── */}
                {step2Done && (
                  <div style={sectionBlockStyle}>
                    <div style={sectionHeadingStyle}>3. Patient Information</div>

                    {step3Done && !isEditingPatient ? (
                      <div style={summaryBoxStyle}>
                        <span style={{ fontSize: "0.95rem", color: "#006b52", fontWeight: "500" }}>
                          {[firstName, lastName].filter(Boolean).join(" ") || "—"}
                          {email && ` · ${email}`}
                          {phone && ` · ${phone}`}
                        </span>
                        <button type="button" onClick={() => setIsEditingPatient(true)} style={changeButtonStyle} aria-label="Change patient details">
                          <IoPencil style={{ fontSize: "1rem" }} />
                          Change
                        </button>
                      </div>
                    ) : (
                      <>
                        <Button variant="link" size="sm" onClick={handleChangePatient} style={{ padding: 0, marginBottom: "12px", fontSize: "0.85rem" }}>
                          Look up different patient
                        </Button>
                        <Row style={{ marginBottom: "20px" }}>
                          <Col md={6}>
                            <Form.Group controlId="firstName">
                              <Form.Label style={labelStyle}>First Name</Form.Label>
                              <Form.Control type="text" placeholder="Enter first name" value={firstName} onChange={e => setFirstName(e.target.value)} style={inputStyle} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="lastName">
                              <Form.Label style={labelStyle}>Last Name</Form.Label>
                              <Form.Control type="text" placeholder="Enter last name" value={lastName} onChange={e => setLastName(e.target.value)} style={inputStyle} />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row style={{ marginBottom: "20px" }}>
                          <Col md={6}>
                            <Form.Group controlId="email">
                              <Form.Label style={labelStyle}>Email</Form.Label>
                              <Form.Control type="email" placeholder="Enter email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="phone">
                              <Form.Label style={labelStyle}>Phone</Form.Label>
                              <Form.Control type="tel" placeholder="+17987456214" value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle} />
                            </Form.Group>
                          </Col>
                        </Row>
                        {step3Done && (
                          <Button type="button" variant="outline-success" size="sm" onClick={() => setIsEditingPatient(false)} style={{ marginTop: "4px" }}>
                            Done
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── Section 4: Shipping Address (visible after patient done) ── */}
                {step3Done && (
                  <div style={sectionBlockStyle}>
                    <div style={sectionHeadingStyle}>4. Shipping Address</div>

                    {step4Done && !isEditingAddress ? (
                      <div style={summaryBoxStyle}>
                        <span style={{ fontSize: "0.95rem", color: "#006b52", fontWeight: "500" }}>
                          {streetAddress}
                          {streetAddress2 ? `, ${streetAddress2}` : ""} · {locality}, {region} {postalCode}
                        </span>
                        <button type="button" onClick={() => setIsEditingAddress(true)} style={changeButtonStyle} aria-label="Change address">
                          <IoPencil style={{ fontSize: "1rem" }} />
                          Change
                        </button>
                      </div>
                    ) : (
                      <>
                        <Form.Group controlId="streetAddress" style={{ marginBottom: "20px" }}>
                          <Form.Label style={labelStyle}>Street Address</Form.Label>
                          <Form.Control type="text" placeholder="Street address" value={streetAddress} onChange={e => setStreetAddress(e.target.value)} style={inputStyle} />
                        </Form.Group>
                        <Row className="g-3" style={{ marginBottom: "12px" }}>
                          <Col md={6}>
                            <Form.Group controlId="streetAddress2" style={{ marginBottom: 0 }}>
                              <Form.Label style={labelStyle}>Address Line 2</Form.Label>
                              <Form.Control type="text" placeholder="Apt, suite, etc." value={streetAddress2} onChange={e => setStreetAddress2(e.target.value)} style={inputStyle} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="postalCode" style={{ marginBottom: 0 }}>
                              <Form.Label style={labelStyle}>Postal Code</Form.Label>
                              <Form.Control type="text" placeholder="Postal code" value={postalCode} onChange={e => setPostalCode(e.target.value)} style={inputStyle} />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group controlId="locality" style={{ marginBottom: 0 }}>
                              <Form.Label style={labelStyle}>City</Form.Label>
                              <Form.Control type="text" placeholder="City" value={locality} onChange={e => setLocality(e.target.value)} style={inputStyle} />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="region" style={{ marginBottom: 0 }}>
                              <Form.Label style={labelStyle}>State</Form.Label>
                              <Form.Select value={region} onChange={e => setRegion(e.target.value)} style={inputStyle}>
                                <option value="">Select State</option>
                                {statesList.map(item => (
                                  <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        {step4Done && (
                          <Button type="button" variant="outline-success" size="sm" onClick={() => setIsEditingAddress(false)} style={{ marginTop: "4px" }}>
                            Done
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* ── Section 5: Shipping & Payment (visible after address done) ── */}
                {step4Done && (
                  <div style={sectionBlockStyle}>
                    <div style={sectionHeadingStyle}>5. Shipping &amp; Payment</div>

                    {step5Done && !isEditingShipping ? (
                      <div style={summaryBoxStyle}>
                        <span style={{ fontSize: "0.95rem", color: "#006b52", fontWeight: "500" }}>
                          {SHIPPING.find(s => s.variantId === shippingMethodId)?.name || "—"}
                          {invoiceAmount ? ` · $${Number(invoiceAmount)}` : ""}
                        </span>
                        <button type="button" onClick={() => setIsEditingShipping(true)} style={changeButtonStyle} aria-label="Change shipping and payment">
                          <IoPencil style={{ fontSize: "1rem" }} />
                          Change
                        </button>
                      </div>
                    ) : (
                      <>
                        <Row className="g-3" style={{ marginBottom: "20px" }}>
                          <Col md={6}>
                            <Form.Group controlId="shippingMethodId" style={{ marginBottom: 0 }}>
                              <Form.Label style={labelStyle}>Shipping Method</Form.Label>
                              <Form.Select value={shippingMethodId} onChange={e => setShippingMethodId(e.target.value)} style={inputStyle}>
                                <option value="">Select shipping method</option>
                                {SHIPPING.map(method => (
                                  <option key={method.variantId} value={method.variantId}>{method.name}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group controlId="invoiceAmount" style={{ marginBottom: 0 }}>
                              <Form.Label style={labelStyle}>Invoice Amount (USD)</Form.Label>
                              <Form.Control type="number" placeholder="0.00" value={invoiceAmount} onChange={e => setInvoiceAmount(e.target.value)} style={inputStyle} />
                            </Form.Group>
                          </Col>
                        </Row>
                        {step5Done && (
                          <Button type="button" variant="outline-success" size="sm" onClick={() => setIsEditingShipping(false)} style={{ marginTop: "4px" }}>
                            Done
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {allStepsDone && (
                  <>
                    <hr style={{ borderColor: "#e8eaed", margin: "28px 0 24px" }} />

                    {/* Submit Button */}
                    <Button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    background: "#007F61",
                    border: "none",
                    borderRadius: "8px",
                    padding: "14px",
                    fontWeight: "600",
                    width: "100%",
                    color: "#fff",
                    marginTop: "12px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    opacity: isSubmitting ? 0.6 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  <IoSend style={{ marginRight: "8px" }} />
                  {isSubmitting ? "Submitting..." : "Submit Order"}
                </Button>
                  </>
                )}
              </Form>
            </Card.Body>
          </Card>
          )}
        </Col>
      </Row>

      {/* Confirmation modal before creating order */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ borderBottom: "2px solid #007F61" }}>
          <Modal.Title style={{ color: "#222", fontWeight: "600" }}>Confirm order details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px 24px 8px" }}>
          <p style={{ color: "#555", marginBottom: "20px", fontSize: "0.95rem" }}>
            Please verify the information below before creating the order.
          </p>
          <div style={{ display: "grid", gap: "16px", fontSize: "0.9rem" }}>
            <div style={{ padding: "12px 16px", background: "#f8f9fa", borderRadius: "8px" }}>
              <div style={{ fontWeight: "600", color: "#444", marginBottom: "8px", textTransform: "uppercase", fontSize: "0.75rem" }}>Program</div>
              <div>
                {FREE_OR_PAID_OPTIONS.find(o => o.key === freeOrPaid)?.label} {PROGRAM_OR_MAINTENANCE_OPTIONS.find(o => o.key === programOrMaintenance)?.label}
                {" — "}
                {SEX_AT_BIRTH_OPTIONS.find(s => s.key === sexAtBirth)?.label}
              </div>
            </div>
            <div style={{ padding: "12px 16px", background: "#f8f9fa", borderRadius: "8px" }}>
              <div style={{ fontWeight: "600", color: "#444", marginBottom: "8px", textTransform: "uppercase", fontSize: "0.75rem" }}>Partner &amp; Vendor</div>
              <div>Partner: {PARTNERS.find(p => p.id === partnerId)?.name || partnerId}</div>
              <div>Vendor: {VENDORS.find(v => v.id === vendorId)?.name || vendorId}</div>
            </div>
            <div style={{ padding: "12px 16px", background: "#f8f9fa", borderRadius: "8px" }}>
              <div style={{ fontWeight: "600", color: "#444", marginBottom: "8px", textTransform: "uppercase", fontSize: "0.75rem" }}>Patient</div>
              <div>{[firstName, lastName].filter(Boolean).join(" ") || "—"}</div>
              <div>{email}</div>
              <div>{phone}</div>
            </div>
            <div style={{ padding: "12px 16px", background: "#f8f9fa", borderRadius: "8px" }}>
              <div style={{ fontWeight: "600", color: "#444", marginBottom: "8px", textTransform: "uppercase", fontSize: "0.75rem" }}>Shipping address</div>
              <div>
                {streetAddress}
                {streetAddress2 ? `, ${streetAddress2}` : ""}
                <br />
                {locality}, {region} {postalCode}
              </div>
            </div>
            <div style={{ padding: "12px 16px", background: "#f8f9fa", borderRadius: "8px" }}>
              <div style={{ fontWeight: "600", color: "#444", marginBottom: "8px", textTransform: "uppercase", fontSize: "0.75rem" }}>Shipping &amp; payment</div>
              <div>Method: {SHIPPING.find(s => s.variantId === shippingMethodId)?.name || "—"}</div>
              <div>Invoice amount: {invoiceAmount ? `$${Number(invoiceAmount)}` : "$0"}</div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #e8eaed", padding: "16px 24px" }}>
          <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)}>
            Back to edit
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmAndCreateOrder}
            disabled={isSubmitting}
            style={{ background: "#007F61", borderColor: "#007F61" }}
          >
            {isSubmitting ? "Creating..." : "Confirm & Create Order"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CreatePrepOrder;
